import { HostMessage, IframeMessage } from './messages.js';
import { PROTOCOL_MAJOR } from './version.js';
// ---------------------------------------------------------------------------
// Envelope
// ---------------------------------------------------------------------------
/**
 * Generate a uuid v4 message id. Uses the platform's `crypto.randomUUID`,
 * which is available in Node ≥ 19 and every modern browser (in secure
 * contexts, which iframes embedded over HTTPS are).
 */
export function createMessageId() {
    return crypto.randomUUID();
}
/**
 * Build a fresh envelope `{ protocol, v, id }`. The id is a uuid v4. Useful
 * when composing a message by hand; the per-message constructors below use
 * this internally.
 */
export function envelope() {
    return {
        protocol: 'rendever-games',
        v: PROTOCOL_MAJOR,
        id: createMessageId(),
    };
}
// Host → Iframe -------------------------------------------------------------
export function hostInit(payload) {
    return { ...envelope(), type: 'init', payload };
}
export function hostPause() {
    return { ...envelope(), type: 'pause' };
}
export function hostResume() {
    return { ...envelope(), type: 'resume' };
}
export function hostQuit() {
    return { ...envelope(), type: 'quit' };
}
export function hostConfigUpdate(payload) {
    return { ...envelope(), type: 'config_update', payload };
}
/**
 * Build a bridge response that correlates with an earlier `request_*` from
 * the iframe via `replyTo`. Pass `{ ok: true }` for success or
 * `{ ok: false, error: '...' }` for failure; optionally include a `payload`
 * for responses that return data (e.g. share dialog completed with a URL).
 */
export function hostBridgeResponse(args) {
    const { replyTo, ok, error, payload } = args;
    // Avoid setting `error`/`payload` to `undefined` because the package is
    // built with `exactOptionalPropertyTypes` and the schema declares them as
    // present-or-absent rather than nullable.
    const msg = {
        ...envelope(),
        type: 'bridge_response',
        replyTo,
        ok,
    };
    if (error !== undefined)
        msg.error = error;
    if (payload !== undefined)
        msg.payload = payload;
    return msg;
}
// Iframe → Host -------------------------------------------------------------
export function iframeReady(payload) {
    return { ...envelope(), type: 'ready', payload };
}
export function iframeSessionStarted(payload) {
    return { ...envelope(), type: 'session_started', payload };
}
export function iframeSessionEnded(payload) {
    return { ...envelope(), type: 'session_ended', payload };
}
export function iframeAchievementUnlocked(payload) {
    return { ...envelope(), type: 'achievement_unlocked', payload };
}
export function iframeQueueEvent(payload) {
    return { ...envelope(), type: 'queue_event', payload };
}
export function iframeRequestHaptic(payload) {
    return { ...envelope(), type: 'request_haptic', payload };
}
export function iframeRequestShare(payload) {
    return { ...envelope(), type: 'request_share', payload };
}
export function iframeRequestClose() {
    return { ...envelope(), type: 'request_close' };
}
export function iframeFocusOut(payload) {
    return { ...envelope(), type: 'focus_out', payload };
}
export function iframeAnalyticsEvent(payload) {
    return { ...envelope(), type: 'analytics_event', payload };
}
export function iframeError(payload) {
    return { ...envelope(), type: 'error', payload };
}
function safeParseWith(schema, input) {
    const r = schema.safeParse(input);
    return r.success ? { ok: true, data: r.data } : { ok: false, error: r.error };
}
/**
 * Parse an incoming message as a HostMessage (host → iframe direction).
 *
 * Use this inside a `window.addEventListener('message', ...)` handler in the
 * iframe. Returns `{ ok: false }` for any input that isn't a valid host
 * message (wrong protocol tag, wrong major, malformed, or actually an iframe
 * message). Does not throw.
 */
export function parseHostMessage(input) {
    return safeParseWith(HostMessage, input);
}
/**
 * Parse an incoming message as an IframeMessage (iframe → host direction).
 *
 * Use this in the host bridge's message handler. Same contract as
 * `parseHostMessage`.
 */
export function parseIframeMessage(input) {
    return safeParseWith(IframeMessage, input);
}
/**
 * Cheap pre-check: does this look like a Rendever-games protocol message at
 * all? Use to short-circuit out of message handlers before the full Zod
 * parse, especially in hot postMessage paths shared with other libraries.
 */
export function isProtocolMessage(input) {
    return (typeof input === 'object' &&
        input !== null &&
        'protocol' in input &&
        input.protocol === 'rendever-games');
}
//# sourceMappingURL=helpers.js.map